import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '../../elements/button'
import Text from '../../elements/Typo/Text'
import { useExperimentApi } from '../../store/api/hooks/experimentApiHook'
import { trackEvent } from '../../_common/functions'
import Loader from '../loader'

interface InconclusiveReasonModalProps {
    literal: string | null
    group_name: string | null
    kpi: string | null
    methodology: string | null
    Reason: string | null
    isModalOpen: boolean
    onClose: () => void
    type: 'no_impact' | 'inconclusive'
}

const InconclusiveReasonModal: React.FC<InconclusiveReasonModalProps> = ({ literal, group_name, kpi, methodology, Reason, isModalOpen, onClose, type }) => {
    const { t } = useTranslation()
    const experimentAPI = useExperimentApi()
    const [reason, setReason] = useState<any>(null)
    const modalRef = useRef<HTMLDialogElement>(null)
    const closeButtonRef = useRef<HTMLButtonElement>(null)

    const getInconclusiveReason = () => {
        experimentAPI.getInconclusiveReason(
            {
                literal,
                group_name,
                kpi,
                methodology,
            },
            (resp: any) => setReason(resp),
            () => {
                trackEvent('inconclusive_reason_load_failed', { experiment_literal: literal })
            },
        )
    }

    const _handleCloseModal = (close_method: 'button' | 'backdrop' | 'escape' = 'button') => {
        trackEvent('inconclusive_reason_modal_closed', { experiment_literal: literal, close_method })
        onClose()
    }

    useEffect(() => {
        if (literal && isModalOpen && !Reason) {
            trackEvent('inconclusive_reason_modal_opened', { experiment_literal: literal })
            getInconclusiveReason()
        }
        if (literal && isModalOpen && Reason) {
            setReason({ data: Reason })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [literal, isModalOpen])

    // Escape key to close modal
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                _handleCloseModal('escape')
            }

            // Basic focus trap
            if (e.key === 'Tab' && modalRef.current) {
                const focusableEls = modalRef.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
                const firstEl = focusableEls[0]
                const lastEl = focusableEls[focusableEls.length - 1]

                if (e.shiftKey) {
                    // Shift + Tab
                    if (document.activeElement === firstEl) {
                        e.preventDefault()
                        lastEl.focus()
                    }
                } else if (document.activeElement === lastEl) {
                    e.preventDefault()
                    firstEl.focus()
                }
            }
        }

        if (isModalOpen) {
            document.addEventListener('keydown', handleKeyDown)
            // Focus first focusable element
            setTimeout(() => closeButtonRef.current?.focus(), 0)
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen])

    return (
        <dialog ref={modalRef} className={`modal ${isModalOpen ? 'modal-open' : ''}`} aria-modal="true" aria-labelledby="inconclusive-modal-title" data-testid="modal">
            <div className="modal-box bg-[#FFFFFF] rounded-[0.833rem] w-md max-w-xl outline-none">
                <div className="bg-[#325A6D1A] rounded-[0.625rem] px-[1.042rem] py-[0.625rem] mb-2">
                    <Text id="inconclusive-modal-title" size="xl" className="font-bold text-center">
                        {t('Why Your Experiment Results Are {{type}}', { type: type === 'no_impact' ? t('No Impact') : t('Inconclusive') })}
                    </Text>
                </div>
                {reason?.data ? (
                    <div className="mt-2 mb-4 bg-red-100 rounded-md p-[10px] border border-red-300">
                        <Text size="md" className="font-normal text-red-900">
                            {reason?.data}
                        </Text>
                    </div>
                ) : (
                    <div className="my-2 flex items-center">
                        <Loader />
                    </div>
                )}

                {type === 'inconclusive' && (
                    <>
                        {[
                            {
                                title: t('1. Insufficient Sample Size'),
                                issue: t('Not enough data to draw reliable conclusions'),
                                bullets: [t('Your experiment needs more users or longer duration to detect meaningful effects'), t('Action: Extend experiment timeline or increase sample size before re-analyzing')],
                            },
                            {
                                title: t('2. Weak Control Group Correlation'),
                                issue: t("Test and control groups don't follow similar baseline patterns"),
                                bullets: [t('Groups behave too differently to make valid comparisons (correlation < 30%)'), t('Action: Review control group selection or adjust matching criteria')],
                            },
                            {
                                title: t('3. High Statistical Bias'),
                                issue: t('Groups remain too different even after corrections'),
                                bullets: [t('Despite our best adjustments, prediction error is still too high (>10%)'), t('Action: Use stricter matching rules or collect more comparable data')],
                            },
                        ].map((section) => (
                            <div className="mt-2" key={section.title}>
                                <Text size="md" className="font-semibold">
                                    {section.title}
                                </Text>
                                <div className="flex">
                                    <Text size="md" className="font-semibold">
                                        {t('Issue:')}
                                    </Text>
                                    &nbsp;
                                    <Text size="md">{section.issue}</Text>
                                </div>
                                {section.bullets.map((bullet) => (
                                    <div className="flex items-center ml-5" key={bullet}>
                                        <div className="w-[1.5rem]">
                                            <div style={{ backgroundColor: '#222222' }} className="rounded-full w-[0.425rem] h-[0.425rem] mx-1"></div>
                                        </div>
                                        <div className="flex-grow">
                                            <Text size="md" className={bullet.startsWith('Action') ? 'font-semibold' : 'font-normal'}>
                                                {bullet}
                                            </Text>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}

                        <div className="mt-3">
                            <Text size="md" className="italic">
                                {t('These are general reasons. For deeper investigation, please contact support or refer to system logs.')}
                            </Text>
                        </div>

                        <div className="mt-4 flex items-center justify-center gap-3">
                            <Button ref={closeButtonRef} className="hover:bg-gray-100" data-testid="close-button" theme="secondary" size="xs" styleType="outline" onClick={() => _handleCloseModal('button')}>
                                {t('I Got It')}
                            </Button>
                        </div>
                    </>
                )}
            </div>

            {/* Interactive backdrop */}
            <div className="modal-backdrop fixed inset-0 bg-black/30" aria-hidden="true" onClick={() => _handleCloseModal('backdrop')}></div>
        </dialog>
    )
}

export default InconclusiveReasonModal
